import React from 'react';
import { ClockCircleOutlined, CloseOutlined } from '@ant-design/icons';
import styled from 'styled-components';
import { CacheStatus, IPipelineRun } from '../../../core/modules/pipelines/pipelines.model';
import StatusBadge from './StatusBadge';
import { annotateScheduleWithLocal, formatRelative } from '../utils/formatStage';

const Card = styled.div`
  position: relative;
  background: #fff;
  border: 1px solid #f0f0f0;
  border-radius: 8px;
  padding: 20px 56px 20px 24px;
  margin-bottom: 16px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
`;

const CloseBtn = styled.button`
  position: absolute;
  top: 14px;
  right: 14px;
  width: 30px;
  height: 30px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  border: 0;
  border-radius: 8px;
  background: rgba(0, 0, 0, 0.04);
  color: rgba(0, 0, 0, 0.55);
  cursor: pointer;
  transition: background 0.15s ease, color 0.15s ease;

  &:hover {
    background: rgba(0, 0, 0, 0.08);
    color: rgba(0, 0, 0, 0.85);
  }

  & .anticon {
    font-size: 14px;
  }
`;

const TitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 6px;
`;

const Title = styled.h3`
  margin: 0;
  font-size: 20px;
  font-weight: 600;
  color: rgba(0, 0, 0, 0.88);
  letter-spacing: -0.2px;
`;

const Meta = styled.div`
  font-size: 12px;
  color: rgba(0, 0, 0, 0.55);
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
`;

const MetaItem = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
`;

interface IHeroHeaderProps {
  title: string;
  schedule: string;
  current: IPipelineRun | null;
  cacheStatus?: CacheStatus;
  fetchedAt?: string;
  description?: string | null;
  onClose?: () => void;
}

const Description = styled.p`
  margin: 8px 0 0;
  font-size: 13px;
  line-height: 1.45;
  color: rgba(0, 0, 0, 0.65);
  max-width: 640px;
`;

const ErrorBanner = styled.p<{ $tone: 'warning' | 'error' }>`
  margin: 12px 0 0;
  padding: 10px 14px;
  border-radius: 6px;
  font-size: 13px;
  line-height: 1.45;
  white-space: pre-wrap;
  word-break: break-word;
  ${({ $tone }) =>
    $tone === 'warning'
      ? 'color: #d48806; background: #fff7e6; border: 1px solid #ffd591;'
      : 'color: #cf1322; background: #fff1f0; border: 1px solid #ffa39e;'}
`;

const HeroHeader: React.FC<IHeroHeaderProps> = ({
  title,
  schedule,
  current,
  description,
  onClose
}) => {
  const status = current?.status ?? 'unknown';

  return (
    <Card>
      <TitleRow>
        <Title>{title}</Title>
        <StatusBadge status={status} />
      </TitleRow>
      <Meta>
        <MetaItem>
          <ClockCircleOutlined />
          {annotateScheduleWithLocal(schedule)}
        </MetaItem>
        {current?.startedAt && (
          <MetaItem>Started {formatRelative(current.startedAt)}</MetaItem>
        )}
      </Meta>
      {description && <Description>{description}</Description>}
      {current?.errorMessage && (
        <ErrorBanner $tone={status === 'warning' ? 'warning' : 'error'}>
          {current.errorMessage}
        </ErrorBanner>
      )}
      {onClose && (
        <CloseBtn type='button' onClick={onClose} aria-label='Close'>
          <CloseOutlined />
        </CloseBtn>
      )}
    </Card>
  );
};

export default HeroHeader;
